import { Info } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

/**
 * Evidence-based testing-protocol tooltip. Renders an info button that opens a
 * popover with the structured protocol (Purpose / Procedure / Recording /
 * Interpretation / References) stored on the existing physical_test_definition.
 * Falls back to the legacy instructions / protocol / safety / equipment fields
 * when no structured `protocol_detail` is present. Read-only — no new schema.
 */
const Section = ({ title, children, testid }) =>
  !children ? null : (
    <div className="mb-2 last:mb-0" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-[#f47a20] font-semibold">{title}</div>
      <div className="text-xs text-zinc-300 leading-snug mt-0.5">{children}</div>
    </div>
  );

export default function ProtocolInfo({ def, slug }) {
  if (!def) return null;
  const pd = def.protocol_detail || {};
  const refs = Array.isArray(pd.references) ? pd.references.filter(Boolean) : [];
  const key = slug || (def.id || "").slice(0, 8);
  const hasAny =
    pd.purpose || pd.procedure || pd.recording || pd.interpretation || refs.length ||
    def.instructions || def.protocol || def.safety_considerations || def.equipment;
  if (!hasAny) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="text-zinc-400 hover:text-[#f47a20] transition-colors shrink-0"
          aria-label={`Protocol for ${def.name}`}
          title="Testing protocol & evidence"
          data-testid={`protocol-info-${key}`}
        >
          <Info size={15} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        collisionPadding={8}
        className="w-80 max-h-[min(70vh,var(--radix-popover-content-available-height))] overflow-y-auto border-white/10 bg-zinc-900 text-zinc-200"
        data-testid={`protocol-popover-${key}`}
      >
        <div className="fl-heading text-sm text-white mb-2">{def.name}</div>
        <Section title="Purpose" testid={`protocol-purpose-${key}`}>{pd.purpose}</Section>
        <Section title="Procedure" testid={`protocol-procedure-${key}`}>{pd.procedure || def.instructions}</Section>
        <Section title="Recording" testid={`protocol-recording-${key}`}>{pd.recording || def.protocol}</Section>
        <Section title="Interpretation" testid={`protocol-interpretation-${key}`}>{pd.interpretation}</Section>
        {def.equipment && <Section title="Equipment" testid={`protocol-equipment-${key}`}>{def.equipment}</Section>}
        {def.safety_considerations && (
          <Section title="Safety">
            <span className="text-amber-400/90">{def.safety_considerations}</span>
          </Section>
        )}
        {refs.length > 0 && (
          <Section title="References" testid={`protocol-references-${key}`}>
            <ul className="list-disc ml-4 space-y-0.5">
              {refs.map((r, i) => <li key={i} className="text-[11px] text-zinc-400">{r}</li>)}
            </ul>
          </Section>
        )}
        <div className="text-[10px] text-zinc-500 mt-2 pt-2 border-t border-white/5">
          Reference protocol only — not a normative threshold or clearance.
        </div>
      </PopoverContent>
    </Popover>
  );
}
